import React from 'react';
import axios from 'axios';
import { Alert, Card, Table, Popconfirm, Button, Modal, Form, Select, Input, InputNumber, Tooltip, message } from 'antd';

import apiPath from 'apiPath';
import stickyError from 'components/stickyError';
import HistoryModal from './HistoryModal';
import NoteModal from './NoteModal';
import AwardInfoModal from './AwardInfoModal';
import DefermentsInfoModal from './DefermentsInfoModal';
import EditAwards from './EditAwards';
import moment from 'components/momentWithZone';

class AddAwardForm extends React.Component {
	handleSubmit = (e) => {
		e.preventDefault();
		this.props.form.validateFields((error, values) => {
			if (error) return stickyError('Validation error', 4.5);
			this.props.onAwardAdd(values);
			this.props.form.resetFields();
		});
	}
	handleTypeChange = (newType) => {
		this.props.form.setFieldsValue({
			amount: parseFloat(this.props.financialAidTypes.find(type => type.id === newType).defaultTotal)
		});
	}
	render() {
		const { getFieldDecorator } = this.props.form;
		return (
			<Card>
				<h3>Add an award</h3>
				<Form onSubmit={this.handleSubmit}>
					<Form.Item label="Type">
						{getFieldDecorator('typeId', {
							initialValue: '',
							rules: [{ required: true, message: 'Please select the award type' }]
						})(
							<Select
								showSearch
								optionFilterProp="title"
								optionLabelProp="title"
								filterOption={(inputValue, option) => {
									if (option.props.title.toLowerCase().indexOf(inputValue.toLowerCase()) !== -1) return true;
									if (option.key.toLowerCase().indexOf(inputValue.toLowerCase()) !== -1) return true;
									return false;
								}}
								onChange={this.handleTypeChange}>
								{this.props.financialAidTypes
									.filter(type => !this.props.awards.find(award => award.award_id === type.id))
									.sort((a, b) => a.description.localeCompare(b.description))
									.map(type => <Select.Option key={type.id} title={type.description}>{type.description}</Select.Option>)
								}
							</Select>
						)}
					</Form.Item>
					<Form.Item label="Amount">
						{getFieldDecorator('amount', {
							initialValue: 0,
							rules: [{
								required: true,
								message: 'Please enter an award amount'
							}, {
								validator: (rule, value, callback) => {
									if (value <= 0) callback('Amount must be greater than 0');
									callback();
								},
								message: 'Please enter a valid award amount'
							}]
						})(
							<InputNumber step={100}
								formatter={value => `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
								parser={value => value.replace(/\$\s?|(,*)/g, '')} />
						)}
					</Form.Item>
					<Button type="primary" htmlType="submit">Add</Button>
				</Form>
			</Card>
		);
	}
}
const WrappedAddAwardForm = Form.create()(AddAwardForm);

class AcceptDecline extends React.Component {
	render() {
		const { record, actOnSelected, financialAidTypes } = this.props;
		const awardType = financialAidTypes.find(type => type.id === record.award_id);
		const acceptLanguage = (awardType && awardType.acceptLanguage) ? awardType.acceptLanguage : 'Are you sure you want to accept this award?'
		return (
			<div>
				<Popconfirm title={acceptLanguage} okText="Accept" onConfirm={() => actOnSelected('acceptance', true, [record.key])}>
					<Button type="primary" disabled={record.disabled}>Accept</Button>
				</Popconfirm>
				<Popconfirm title={'Are you sure you want to decline this award?'} okText="Decline" onConfirm={() => actOnSelected('acceptance', false, [record.key])}>
					<Button disabled={record.disabled}>Decline</Button>
				</Popconfirm>
			</div>
		);
	}
}

class UpdateableAmountForm extends React.Component {
	state = {
		touched: false,
		originalAmount: null
	}
	handleSubmit = (e) => {
		e.preventDefault();
		this.props.form.validateFields((error, values) => {
			if (error) return stickyError('Validation error', 4.5);
			axios.put(apiPath + '/financialaid', values, { withCredentials: true }).then(result => {
				this.setState({ touched: false })
				this.props.getAwards();
			}).catch(error => {
				stickyError('An error occurred while trying to update the award amount');
				console.error(error);
			});
		});
	}
	componentWillReceiveProps(nextProps) {
		this.setState({ originalAmount: nextProps.amount });
	}
	componentDidMount() {
		this.setState({ originalAmount: this.props.amount });
	}
	render() {
		const { getFieldDecorator } = this.props.form;
		const disabled = !this.state.touched || parseInt(this.props.form.getFieldValue('amount')) === parseInt(this.state.originalAmount);
		return (
			<Form layout="inline" onSubmit={this.handleSubmit}>
				<Form.Item>
					{getFieldDecorator('award_id', {
						initialValue: this.props.award_id
					})(
						<Input type="hidden" />
					)}
				</Form.Item>
				<Form.Item>
					{getFieldDecorator('amount', {
						initialValue: this.props.amount
					})(
						<InputNumber step={100} disabled={this.props.disabled}
							onChange={() => this.setState({ touched: true })}
							formatter={value => `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
							parser={value => value.replace(/\$\s?|(,*)/g, '')} />
					)}
				</Form.Item>
				<Tooltip title="Update amount">
					<Button icon="check" type="primary" htmlType="submit"
						disabled={disabled} />
				</Tooltip>
			</Form>
		);
	}
}
const WrappedUpdateableAmountForm = Form.create()(UpdateableAmountForm);

export default class FinancialAid extends React.Component {
	state = {
		showModal: false,
		showDefermentsModal: false,
		selectedRowKeys: [],
		hasSelectedAnticipated: false,
		columns: [
			{
				title: 'Type', dataIndex: 'award_id',
				render: value => {
					const type = this.state.financialAidTypes.find(type => type.id === value);
					return (type) ? type.description : '';
				}
			},
			{
				title: 'Amount', dataIndex: 'amount', variants: ['admin'],
				render: (value, record) =>
					<WrappedUpdateableAmountForm
						award_id={record.award_id} amount={value}
						// disabled={record.accepted_date !== null}
						getAwards={this.getAwards} />
			},
			{
				title: 'Amount', dataIndex: 'amount', variants: ['normal'],
				render: value => `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
			},
			{ title: 'Status', dataIndex: 'status', variants: ['admin', 'normal'] },
			/*
			{
				title: 'Actions', dataIndex: 'accepted_date', variants: ['normal'],
				render: (value, record) => (record.status === 'Awarded') ? <AcceptDecline financialAidTypes={this.state.financialAidTypes} actOnSelected={this.actOnSelected} record={record} /> : 'none'
			},
			*/
			{
				title: 'History', dataIndex: 'history', variants: ['admin'],
				render: (value, record) => <HistoryModal history={value} current={record} />
			},
			{
				title: 'Note', dataIndex: 'note',
				render: (value, record) => <span title={value}>
					<NoteModal
						award={record}
						awardTypes={this.state.awardTypes}
						variant={this.props.variant}
						onNoteUpdate={this.handleNoteUpdate} />
				</span>
			}
		],
		financialAidAwards: [],
		financialAidTypes: [],
		deferredCodes: []
	}
	getTypes = () => {
		axios.get(apiPath + '/financialaid/types').then(result => {
			this.setState({
				financialAidTypes: result.data,
				deferredCodes: result.data
					.filter(type => type.category === 'DEFER')
					.map(type => type.id)
			});
		});
	}
	getAwards = () => {
		axios.get(apiPath + '/financialaid', { withCredentials: true }).then(result => {
			this.setState({
				financialAidAwards: result.data
					.filter(award => award.current === "Y")
					.map(award => {
						award.key = award.id;
						award.history = result.data.filter(otherAward => otherAward.current !== "Y" && otherAward.award_id === award.award_id);
						return award;
					})
			});
		});
	}
	handleNoteUpdate = (id, noteText) => {
		return this.processRequests([
			axios.put(apiPath + '/financialaid/awardnote', {
				award_id: id.split('_')[0],
				note: noteText
			}, { withCredentials: true })
		]);
	}
	addAward = (award) => {
		axios.post(apiPath + '/financialaid', {
			award_id: award.typeId,
			amount: award.amount
		}, { withCredentials: true }).then(result => {
			this.getAwards();
		});
	}
	processRequests = (requests) => {
		return axios.all(requests).then(result => {
			this.getAwards();
			this.setState({
				selectedRowKeys: []
			});
			message.success('Saved!');
		}).catch(error => {
			stickyError('Error updating awards');
			console.error(error);
		});
	}
	actOnSelected = (route, value, selectedRowKeys) => {
		let requests = [];
		selectedRowKeys.map(key => {
			let data = this.state.financialAidAwards.find(award => award.id === key);
			if (!data) return;
			else if (route === 'acceptance') data.accepted = value;
			else if (route === 'approval') data.approved = value;
			if (route === 'delete') return requests.push(axios.delete(apiPath + '/financialaid/' + route, { data: data, withCredentials: true }));
			else return requests.push(axios.put(apiPath + '/financialaid/' + route, data, { withCredentials: true }));
		});
		this.processRequests(requests);
	}
	approveSelected = () => this.actOnSelected('approval', true, this.state.selectedRowKeys);
	disapproveSelected = () => this.actOnSelected('approval', false, this.state.selectedRowKeys);
	acceptSelected = () => this.actOnSelected('acceptance', true, this.state.selectedRowKeys);
	declineSelected = () => this.actOnSelected('acceptance', false, this.state.selectedRowKeys);
	deleteSelected = () => this.actOnSelected('delete', true, this.state.selectedRowKeys);
	handleRowSelectionChange = (selectedRowKeys) => {
		this.setState({
			selectedRowKeys: selectedRowKeys,
			hasSelectedAnticipated: selectedRowKeys.filter(key => this.state.financialAidAwards.find(award => award.key === key && award.status === 'Anticipated')).length > 0
		});
	}
	componentWillMount() {
		this.getTypes();
		this.getAwards();
	}
	render() {
		const disabled = this.props.variant !== 'admin' && !this.props.sectionIsEnabled(this.props, 'financial/financialaid');
		if (this.props.variant !== 'admin' && this.props.redirectIfHidden) {
			const redirect = this.props.redirectIfHidden(this.props, 'financial/financialaid');
			if (redirect) return redirect;
		}

		const normalAwards = this.state.financialAidAwards
			.filter(award => this.state.deferredCodes.indexOf(award.award_id) === -1)
			.map(award => {
				award.disabled = disabled;
				award.type = this.state.financialAidTypes.find(type => type.id === award.award_id);
				return award;
			});
		const deferredAwards = this.state.financialAidAwards
			.filter(award => this.state.deferredCodes.indexOf(award.award_id) !== -1)
			.map(award => {
				award.disabled = disabled;
				award.type = this.state.financialAidTypes.find(type => type.id === award.award_id);
				return award;
			});

		const hasSelected = this.state.selectedRowKeys.length > 0;
		const financialRule = this.props.variant === 'admin' || moment().isAfter('2018-10-12T17:30')
		return (
			<div>
				{moment().isBefore('2018-10-12T17:30') &&
					<Alert showIcon type="info"
						message="Please Note"
						description={<span>The Financial Aid section has been disabled while our Financial Aid Office to reviews and posts scholarships to student accounts. All scholarships will be posted and finalized on October 12th. At this time, the Financial Aid section of MyVoyage will re-open. If you have questions about your financial aid package, please e-mail <a target="blank" href="mailto:financialaid@isevoyages.org">financialaid@isevoyages.org.</a></span>} />
				}

				{financialRule &&
					<div>
						{this.props.variant === 'normal' && disabled && <h2 className="withSpacing">This section is currently disabled</h2>}
						{/*
				<Alert showIcon type="info"
						message="Please Note"
						description="You must accept all scholarships and deferments in order for these amounts to be applied to your overall program fee balance." />
				<br />
				<Alert showIcon type="info"
						message="Notice"
						description={<span>If you are receiving aid from your home school we will need verification of your award. Bring this form to your financial aid office and have them fill it out so that we can defer finalized aid.  The Financial Aid Notification form is not required for students attending a Direct Bill Institution, Colorado State University or Davis UWC Scholars.<a target="blank" href="http://www.semesteratsea.org/wp-content/uploads/2018/01/FAN2.0.pdf
						"><br />http://www.semesteratsea.org/wp-content/uploads/2018/01/FAN2.0.pdf</a>.</span>} />
				<br />
				<Alert showIcon type="info"
						message="Notice"
						description={<span>If your university financial aid or study abroad office has concerns about completing the FAN, please present them with the Financial Aid Verification Form (FAV) and complete the Student Specific Financial Aid Disclosure form (SSFA).<a target="blank" href="http://www.semesteratsea.org/wp-content/uploads/2019/10/FAV-form-1920.pdf">
							<br />http://www.semesteratsea.org/wp-content/uploads/2019/10/FAV-form-1920.pdf</a>. <br /> <a target="blank" href="http://www.semesteratsea.org/wp-content/uploads/2018/01/SSFAform.pdf">http://www.semesteratsea.org/wp-content/uploads/2018/01/SSFAform.pdf</a>.</span> } />
				<br />
				*/}
						{this.state.financialAidAwards.find(award => award.status === 'Accepted') &&
							<Alert showIcon type="success"
								message="Thank you for accepting your award!"
								description={<span>
									As a recipient of Semester at Sea scholarship funds, you are expected to hold yourself to the highest standards when it comes to your work performance and personal demeanor. You are expected to abide by all policies and procedures listed in the voyager’s handbook for the duration of the voyage, and to remain fully engaged in the academic program for the duration of the voyage.
									<br />
									If these expectations are not met, your scholarship funds may be rescinded, with or without forfeiture of the remaining pro-rated financial aid award.
								</span>} />
						}
						{this.props.variant === 'admin' &&
							<div style={{ paddingBottom: '20px' }}>
								Set the status of selected awards to:&nbsp;
								<Button
									disabled={!hasSelected}
									onClick={this.disapproveSelected}>
									Anticipated
								</Button>
								<Button type="primary"
									disabled={!hasSelected}
									onClick={this.approveSelected}>
									Awarded
								</Button>
								<Button
									disabled={!hasSelected || this.state.hasSelectedAnticipated}
									title={this.state.hasSelectedAnticipated && 'You must set an award\'s status to "Awarded" before you can accept the award'}
									onClick={this.acceptSelected}>
									Accepted
								</Button>
								<Popconfirm
									title={'Are you sure you want to delete the selected award(s)?'}
									onConfirm={this.deleteSelected}>
									<Button type="danger"
										disabled={!hasSelected}>
										Deleted
									</Button>
								</Popconfirm>
							</div>
						}
						<h3 className="withSpacing">Awards
							<Button icon="question-circle"
								onClick={() => this.setState({ showAwardModal: true })} />
						</h3>
						<Table
							size="middle"
							pagination={false}
							columns={this.state.columns.filter(col => !col.variants || col.variants.indexOf(this.props.variant) !== -1)}
							rowSelection={(this.props.variant === 'admin') ? {
								selectedRowKeys: this.state.selectedRowKeys,
								// getCheckboxProps: row => ({ disabled: row.accepted_date !== null }),
								onChange: this.handleRowSelectionChange
							} : null}
							dataSource={normalAwards} />
						<AwardInfoModal
							visible={this.state.showAwardModal}
							onCancel={() => {
								this.setState({ showAwardModal: false });
								this.getTypes();
							}} />
						<DefermentsInfoModal
							visible={this.state.showDefermentsModal}
							onCancel={() => this.setState({ showDefermentsModal: false })} />
						{deferredAwards.length > 0 &&
							<div>
								<br />
								<h3 className="withSpacing">Deferments
									<Button icon="question-circle"
										onClick={() => this.setState({ showDefermentsModal: true })} />
								</h3>
								<Alert showIcon type="warning"
									description="These deferred payments indicate that you have an outstanding balance that is scheduled to be paid by some method of financial aid, institutional direct bill agreement, or payment plan. While we recognize that funds may not always be available at the time of sailing, you must recognize that it is your responsibility to make sure that the funds are forwarded to ISE after disbursement. Please be aware that the ramification of non-payment may include suspension of your shipboard account, dismissal from the program at your own expense, having your transcript held, or being reported to collections post-voyage." />
								<Table
									size="middle"
									pagination={false}
									columns={this.state.columns.filter(col => !col.variants || col.variants.indexOf(this.props.variant) !== -1)}
									rowSelection={(this.props.variant === 'admin') ? {
										selectedRowKeys: this.state.selectedRowKeys,
										// getCheckboxProps: row => ({ disabled: row.accepted_date !== null }),
										onChange: this.handleRowSelectionChange
									} : null}
									dataSource={deferredAwards} />
							</div>
						}
						<br />
						{this.props.variant === 'admin' &&
							<div>
								<br /><br />
								<EditAwards onAwardUpdate={() => this.getTypes()} financialAidTypes={this.state.financialAidTypes} />
								<br />
								<WrappedAddAwardForm
									financialAidTypes={this.state.financialAidTypes}
									awards={this.state.financialAidAwards}
									onAwardAdd={this.addAward} />
							</div>
						}
					</div>
				}
			</div>
		);
	}
}


// WEBPACK FOOTER //
// ./portal-components/Portal/Financial/FinancialAid/index.jsx